/**
 * CSRF double-submit, for the cookie rail.
 *
 * The session cookie is HttpOnly and `SameSite` alone is not the whole story,
 * so the backend runs a double-submit guard: `GET /api/v1/auth/csrf` sets a
 * NON-HttpOnly `csrf_token` cookie, and every state-changing request must echo
 * that value in `X-CSRF-Token`. A mismatch is a 403 with
 * `detail: "csrf_token_required"`.
 *
 * This is not optional. The backend REFUSES TO START in production unless
 * `CSRF_PROTECTION_ENFORCED=1`, and under that flag every POST/PUT/PATCH/DELETE
 * from a cookie-authenticated client without the header is rejected. Verified
 * before writing this: with the flag on, creating a calendar event answered 403
 * and no `csrf_token` cookie had ever been issued.
 *
 * WHAT DOUBLE-SUBMIT ACTUALLY CHECKS — worth stating, because it is easy to
 * get wrong: the server compares the COOKIE to the HEADER. It does not
 * validate the token against any server-side state. Two matching garbage
 * values pass; that is by design, since an attacker on another origin cannot
 * read the cookie to build the header. So there is no such thing as a "stale"
 * token here — only a MISSING one.
 *
 * Which is why the pre-flight `ensureCsrfToken()` is the real fix: it runs
 * before every guarded request and seeds when no cookie exists. Verified —
 * deleting the cookie mid-session and writing again succeeds first try,
 * because the seed happens before the send.
 *
 * The 403 retry below is a narrow backstop for the remaining race: the cookie
 * disappearing between that check and the send, or a proxy stripping the
 * header. It is easy to write and never test, so it was tested by corrupting
 * the header on the first write only — 403, re-seed, 201, and nothing shown to
 * the user. The old client had no recovery at all.
 *
 * The token is read AT SEND TIME rather than cached in a module variable, so a
 * replay picks up the value the re-seed just wrote.
 *
 * Bearer-authenticated requests (the Capacitor native rail) are exempt
 * server-side: no cookie was used, so there is no CSRF risk. We skip the seed
 * entirely for them rather than making a pointless network call on a phone.
 */

const COOKIE = "csrf_token";
const SEED_URL = "/api/v1/auth/csrf";

/** Methods the backend guards (_STATE_CHANGING_METHODS). */
const GUARDED = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function isGuardedMethod(method: string): boolean {
  return GUARDED.has(method.toUpperCase());
}

/** The token the browser currently holds, or null. Never cached. */
export function readCsrfCookie(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    // No document (SSR, worker) — the caller falls through to no header, and
    // the server decides.
    return null;
  }
}

/** In-flight seed, so a burst of writes triggers ONE request, not five. */
let seeding: Promise<void> | null = null;

/**
 * Make sure a token exists. Idempotent and single-flight.
 *
 * `force` re-seeds even when a cookie is present — used after a CSRF 403,
 * where the cookie we hold is precisely the one the server just rejected.
 */
export async function ensureCsrfToken(force = false): Promise<void> {
  if (!force && readCsrfCookie()) return;
  if (seeding) return seeding;

  seeding = (async () => {
    try {
      // Auth-free by design: callable before login, so a fresh tab can hold a
      // token before its first write.
      await fetch(SEED_URL, { method: "GET", credentials: "include" });
    } catch {
      // Offline, or the endpoint is unreachable. Non-fatal: the request goes
      // out without the header and the server answers. Failing here would turn
      // a connectivity blip into a broken form.
    } finally {
      seeding = null;
    }
  })();

  return seeding;
}

/** Is this the server telling us our token is missing or stale? */
export function isCsrfRejection(status: number, payload: unknown): boolean {
  if (status !== 403) return false;
  const detail = (payload as { detail?: unknown } | null)?.detail;
  return detail === "csrf_token_required";
}

export { COOKIE as CSRF_COOKIE };
